import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { blankLink } from 'config';
import { Col, Form, FormGroup, Row } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IBFProductVariable, ContainerFieldItem } from 'Services/widgets/interfaces';
import { monthList, OptionDateItem } from '../../bf-hooks';
import { isRequiredField } from '../../booking-funnel/booking-funnel-hooks';

dayjs.extend( customParseFormat );

export interface DateFieldProps {
  valueField: string;
  variable: IBFProductVariable;
  showTooltip: ( isShow: boolean, tooltip: string ) => void;
  isShortName?: boolean;
  stepItem?: ContainerFieldItem;
}

const defaultDay = '1';
const countMonths = 24;

export const DateField: React.FC<DateFieldProps> = ( {
  valueField,
  variable,
  showTooltip,
  isShortName,
  stepItem,
} ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const { errors, control, setValue, getValues } = useFormContext();
  const monthOptions = monthList();
  const defaultDate = dayjs( new Date() ).format( );
  const fullDate = dayjs( valueField, 'YYYY-MM-DD' );

  const [ monthDate, setMonthDate ] = React.useState<string>( valueField ? fullDate.format( 'M' ) : '' );
  const [ yearDate, setYearDate ] = React.useState<string>( valueField ? fullDate.format( 'YYYY' ) : '' );

  const yearOptions = React.useMemo<OptionDateItem[]>( () => {
    const years: OptionDateItem[] = [];

    const maxYear = dayjs().year();
    const minYear = dayjs().subtract( countMonths, 'months' ).year();

    for ( let i = minYear; i <= maxYear; i += 1 ) {
      const yearItem: OptionDateItem = {
        value: i,
        label: String( i ),
      };

      years.push( yearItem );
    }

    return years;
  }, [] );

  const isValidDateField = ( dateVal: string ): boolean => {
    const currentData = dayjs( dateVal );
    const currentMonth = dayjs().endOf( 'month' );

    if ( !currentMonth.isAfter( currentData ) ) {
      return false;
    }

    const currentMonthStartOf = dayjs().startOf( 'month' );
    const twoYearAgo = dayjs( currentMonthStartOf ).subtract( countMonths, 'months' );
    if ( !twoYearAgo.isBefore( currentData ) ) {
      return false;
    }

    return true;
  };

  const handleChangeMonth = ( e: React.ChangeEvent<HTMLSelectElement>, nameField: string ) => {
    const month = e.target.value;
    if ( !month ) {
      setValue( nameField, '', { shouldValidate: true } );
      setMonthDate( '' );
      return;
    }

    const currentValue: string = getValues( nameField );
    let valDate: string = defaultDate;
    if ( currentValue && dayjs.isDayjs( valDate ) ) {
      valDate = currentValue;
    }

    let year = dayjs( valDate ).year().toString();
    if ( yearDate ) {
      year = yearDate;
    } else {
      setYearDate( year );
    }

    const valueDate: string = dayjs( valDate )
      .set( 'date', Number( defaultDay ) )
      .set( 'month', Number( month ) - 1 )
      .set( 'year', Number( year ) ).format( );

    setMonthDate( month );

    setValue( nameField, valueDate, { shouldValidate: true } );
  };

  const handleChangeYear = ( e: React.ChangeEvent<HTMLSelectElement>, nameField: string ) => {
    const year = e.target.value;
    if ( !year ) {
      setValue( nameField, '', { shouldValidate: true } );
      setYearDate( '' );
      return;
    }

    const currentValue: string = getValues( nameField );
    let valDate: string = defaultDate;
    if ( currentValue ) {
      valDate = currentValue;
    }

    let month = '1';
    if ( monthDate ) {
      month = monthDate;
    } else {
      setMonthDate( month );
    }

    const valueDate: string = dayjs( valDate )
      .set( 'date', Number( defaultDay ) )
      .set( 'month', Number( month ) )
      .set( 'year', Number( year ) ).format( );

    setYearDate( year );

    setValue( nameField, valueDate, { shouldValidate: true } );
  };

  return (
    <Fragment>
      <Controller
        name={ isShortName ? variable.name :
          `${variable.name}_${variable.groupName ? variable.groupName : variable.insuredObjectId}` }
        control={ control }
        rules={ {
          required: isRequiredField( variable, stepItem ),
          validate: ( value ) => {
            if( !isValidDateField( value ) ) {
              const errMessage = variable.bfTooltip ? variable.bfTooltip : t( 'bookingFunnel.tariff.notCorrectDateGM' );
              return errMessage;
            }
          },
        } }
        defaultValue={ valueField || '' }
        render={ ( props ) => (
          <FormGroup className="date-picker-field" controlId={ props.name }>
            <Form.Label id={ `${props.name}-label` } className={ variable.bfTooltip && 'tooltip-label' }>
              { variable.bfLabel ?
                <div dangerouslySetInnerHTML={ { __html: `${variable.bfLabel}` } }></div>
                : variable.label }
              { variable.bfTooltip && (
                <a
                  id={ `tooltip-${variable.name}-${variable.insuredObjectId}` }
                  href={ blankLink }
                  onClick={ ( e ) => {
                    e.preventDefault();
                    showTooltip( true, variable.bfTooltip! );
                  } }
                >
                  { t( 'bookingFunnel.tooltipHelp' ) }
                </a>
              ) }
            </Form.Label>
            { variable.bfDescription && (
              <span className="gm-info f-12 mt-1">
                { variable.bfDescription }
              </span>
            ) }
            <Row className="date-container">
              <Col md={ 6 }>
                <Form.Control
                  as="select"
                  className="form-select"
                  value={ monthDate }
                  onChange={ ( e: React.ChangeEvent<HTMLSelectElement> ) => handleChangeMonth( e, props.name ) }
                  placeholder={ t( 'bookingFunnel.tariff.selectMonth' ) }
                  isInvalid={ errors[props.name] !== undefined }
                >
                  <option value="">{ t( 'bookingFunnel.tariff.selectMonth' ) }</option>
                  { monthOptions.map( ( el ) =>
                    <option
                      key={ el.value }
                      value={ el.value }
                    >
                      { el.label }
                    </option> ) }
                </Form.Control>
              </Col>
              <Col md={ 6 }>
                <Form.Control
                  as="select"
                  className="form-select"
                  value={ yearDate }
                  placeholder={ t( 'bookingFunnel.tariff.selectYear' ) }
                  onChange={ ( e: React.ChangeEvent<HTMLSelectElement> ) => handleChangeYear( e, props.name ) }
                  isInvalid={ errors[props.name] !== undefined }
                >
                  <option value="">{ t( 'bookingFunnel.tariff.selectYear' ) }</option>
                  { yearOptions.sort( ( a, b ) => b.value - a.value ).map( ( el ) =>
                    <option
                      key={ el.value }
                      value={ el.value }
                    >
                      { el.label }
                    </option> ) }
                </Form.Control>
              </Col>
            </Row>
            <Form.Control
              type="hidden"
              isInvalid={ errors[props.name] !== undefined }
            />
            <Form.Control.Feedback type="invalid">
              { errors[props.name]?.message ? (
                <div dangerouslySetInnerHTML={ { __html: `${errors[props.name]?.message}` } }></div>
              ) : (
                <Fragment>
                  { t( 'base:forms.messages.fieldRequired',
                    { fieldLabel: variable.label } ) }
                </Fragment>
              ) }
            </Form.Control.Feedback>
          </FormGroup>
        ) }
      />
    </Fragment>
  );
};
